/**
 * Composer — first optimistic mutation surface.
 *
 * Two tabs (design handoff screen-ticket.jsx):
 *   - Reply: outbound `staff_email` — visible to client
 *   - Internal note: `internal_note` — visible to staff only, amber tint
 *
 * Send rules (INTERACTION_SPEC §3 + §6):
 *   - Cmd/Ctrl+Enter sends
 *   - Button disables on click; re-enables on response or 8 s timeout
 *   - Empty input blocks send (validation on blur, not keystroke)
 *   - On send: textarea clears immediately (optimistic), focus stays
 */

import {
  ArrowUpDown,
  ChevronDown,
  Clock,
  FileType,
  Mail,
  NotebookPen,
  Paperclip,
  Send,
  User,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import type { EmailTemplate } from '@beacon/core';

import { Button } from '@/components/ui/button.js';
import { Textarea } from '@/components/ui/textarea.js';
import { useEmailTemplates } from '@/hooks/use-email-templates.js';
import {
  useSendMessage,
  type CachedMessage,
} from '@/hooks/use-messages.js';
import { cn } from '@/lib/cn.js';
import { focusSidePanelSection } from '@/lib/focus-side-section.js';

const MAX_FILES = 5;
const MAX_SINGLE_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_BYTES = 25 * 1024 * 1024;

type Tab = 'reply' | 'note';

export function Composer({
  ticketNumber,
}: {
  ticketNumber: string;
}) {
  const [tab, setTab] = useState<Tab>('reply');
  const [body, setBody] = useState('');
  const [showBlankError, setShowBlankError] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sendMessage = useSendMessage();
  const templatesQuery = useEmailTemplates();

  /** Insert a template body at the textarea caret. If the textarea is
   *  empty we replace; otherwise we splice at the cursor (or append if
   *  there's no live cursor). The DOM `setRangeText` keeps undo history
   *  intact, so ⌘Z reverts the insertion. */
  function insertTemplate(template: EmailTemplate) {
    const ta = textareaRef.current;
    if (!ta) return;
    const text = template.body;
    if (body.trim() === '') {
      setBody(text);
      // Move caret to the end so the next keystroke continues the body.
      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(text.length, text.length);
      });
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const next = body.slice(0, start) + text + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      ta.focus();
      const cursor = start + text.length;
      ta.setSelectionRange(cursor, cursor);
    });
  }

  function addFiles(incoming: FileList | File[]) {
    const next = [...files];
    let error: string | null = null;
    for (const f of Array.from(incoming)) {
      if (next.length >= MAX_FILES) {
        error = `At most ${MAX_FILES} files per message`;
        break;
      }
      if (f.size === 0) continue;
      if (f.size > MAX_SINGLE_BYTES) {
        error = `${f.name} is over ${MAX_SINGLE_BYTES / 1024 / 1024} MB`;
        break;
      }
      const total = next.reduce((sum, x) => sum + x.size, 0) + f.size;
      if (total > MAX_TOTAL_BYTES) {
        error = `Total upload would exceed ${MAX_TOTAL_BYTES / 1024 / 1024} MB`;
        break;
      }
      // De-dupe by name + size — same file picked twice shouldn't show twice.
      const dup = next.some(
        (x) => x.name === f.name && x.size === f.size
      );
      if (dup) continue;
      next.push(f);
    }
    setFiles(next);
    setFileError(error);
  }

  function removeFile(idx: number) {
    setFiles(files.filter((_, i) => i !== idx));
    setFileError(null);
  }

  // Watchdog re-enables the button after 8 s (INTERACTION_SPEC §6).
  const isPending = sendMessage.isPending;
  useEffect(() => {
    if (!isPending) return;
    const id = setTimeout(() => sendMessage.reset(), 8000);
    return () => clearTimeout(id);
  }, [isPending, sendMessage]);

  function send() {
    const text = body.trim();
    if (!text) {
      setShowBlankError(true);
      textareaRef.current?.focus();
      return;
    }
    setShowBlankError(false);

    sendMessage.mutate({
      ticketNumber,
      input: {
        type: tab === 'reply' ? 'staff_email' : 'internal_note',
        bodyText: text,
        bodyHtml: null,
      },
      ...(files.length > 0 ? { files } : {}),
    });

    // Optimistic clear — message has already been added to the cache.
    setBody('');
    setFiles([]);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    textareaRef.current?.focus();
  }

  function onKey(e: KeyboardEvent<HTMLTextAreaElement>) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      send();
    }
  }

  return (
    <div className="border-t border-ink-600 bg-ink-850">
      <div className="px-6 pt-2 flex items-center gap-1">
        <TabButton active={tab === 'reply'} onClick={() => setTab('reply')}>
          <Mail size={13} /> Reply
        </TabButton>
        <TabButton
          active={tab === 'note'}
          onClick={() => setTab('note')}
          variant="note"
        >
          <NotebookPen size={13} /> Internal note
        </TabButton>
      </div>

      <div
        className={cn(
          'px-6 py-3 border-t',
          tab === 'reply'
            ? 'border-ink-600'
            : 'border-warn-500/30 bg-warn-500/[0.04]'
        )}
      >
        <div className="flex items-center gap-2 text-[12px] text-ink-300 mb-2">
          {/* To/Cc chips — pre-textarea so staff can see the recipient
              before they hit send (Decision #14 / audit #14). The
              actual recipient address is computed server-side at send
              time from the contract's organisation primary contact +
              plus-address Reply-To, so this stays a visual anchor for
              now. Cc edit lands with the dialog rebuild later. */}
          {tab === 'reply' ? (
            <>
              <span>To:</span>
              <span className="px-1.5 h-5 rounded bg-ink-700 text-ink-100 inline-flex items-center text-[12px]">
                Client primary contact
              </span>
              <span className="ml-2">Cc:</span>
              <button
                type="button"
                disabled
                className="text-ink-400 hover:text-ink-200 disabled:cursor-not-allowed"
                title="Adding cc — coming with the dialog rebuild"
              >
                + Add
              </button>
            </>
          ) : null}
          <span className="ml-auto mono text-[11.5px]">
            {tab === 'note' ? (
              <span className="text-warn-400">⚠ Visible to staff only</span>
            ) : (
              <span className="text-ink-400">Customer will see this email</span>
            )}
          </span>
        </div>

        <Textarea
          ref={textareaRef}
          rows={3}
          placeholder={
            tab === 'reply'
              ? 'Write your reply…'
              : 'Internal note — not visible to the client'
          }
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            if (showBlankError) setShowBlankError(false);
          }}
          onBlur={() => {
            // Validation runs on blur per INTERACTION_SPEC §6.
            if (body.trim() === '') setShowBlankError(false);
          }}
          onKeyDown={onKey}
          className={cn(
            tab === 'note' && 'bg-warn-500/5 border-warn-500/30',
            showBlankError && 'border-danger-500/50'
          )}
        />

        {files.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-md bg-ink-800 border border-ink-600 pl-2 pr-1 py-0.5 text-[12px] text-ink-100"
              >
                <Paperclip size={11} className="text-ink-300" />
                <span className="truncate max-w-[14rem]">{f.name}</span>
                <span className="text-ink-400 mono text-[11px]">
                  {formatBytes(f.size)}
                </span>
                <button
                  onClick={() => removeFile(i)}
                  disabled={isPending}
                  aria-label={`Remove ${f.name}`}
                  className="text-ink-300 hover:text-ink-100 focus-ring rounded p-0.5 disabled:opacity-50"
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {fileError && (
          <p className="mt-1.5 text-[11.5px] text-danger-400">{fileError}</p>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="sr-only"
          aria-label="Attach files"
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
          }}
        />

        <div className="flex items-center gap-1 mt-2">
          <ToolbarButton
            onClick={() => fileInputRef.current?.click()}
            disabled={isPending || files.length >= MAX_FILES}
            label="Attach files"
            icon={<Paperclip size={13} />}
          >
            Attach
          </ToolbarButton>
          <TemplatesMenu
            templates={(templatesQuery.data ?? []).filter(
              (t) => t.scope === tab
            )}
            disabled={isPending}
            onPick={insertTemplate}
          />
          {/* Quick actions — these duplicate the side-panel dropdowns
              so staff can act mid-reply without losing focus. v1 wires
              them as scroll-into-view anchors targeting the side panel
              sections; the polish slice replaces with inline popovers. */}
          {tab === 'reply' && (
            <>
              <ToolbarButton
                onClick={() => focusSidePanelSection('hours')}
                label="Log time"
                icon={<Clock size={13} />}
              >
                Log time
              </ToolbarButton>
              <ToolbarButton
                onClick={() => focusSidePanelSection('status')}
                label="Change status"
                icon={<ArrowUpDown size={13} />}
              >
                Change status
              </ToolbarButton>
              <ToolbarButton
                onClick={() => focusSidePanelSection('assignee')}
                label="Assign"
                icon={<User size={13} />}
              >
                Assign
              </ToolbarButton>
            </>
          )}
          <span className="ml-auto text-[11.5px] text-ink-400 mono">
            ⌘↵ send
          </span>
          {tab === 'reply' ? (
            <Button
              variant="primary"
              size="md"
              onClick={send}
              disabled={isPending}
            >
              <Send size={13} />
              {isPending ? 'Sending…' : 'Send reply'}
            </Button>
          ) : (
            <Button
              variant="default"
              size="md"
              onClick={send}
              disabled={isPending}
            >
              <NotebookPen size={13} />
              {isPending ? 'Saving…' : 'Save note'}
            </Button>
          )}
        </div>

        {showBlankError && (
          <div className="mt-1.5 text-[11.5px] text-danger-400">
            Type a message before sending.
          </div>
        )}
      </div>
    </div>
  );
}

function ToolbarButton({
  onClick,
  disabled,
  label,
  icon,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12.5px] text-ink-300 hover:text-ink-100 hover:bg-ink-800 focus-ring disabled:opacity-50"
    >
      {icon}
      {children}
    </button>
  );
}


function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function TabButton({
  active,
  onClick,
  children,
  variant,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'note';
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'h-8 px-3 text-[13px] font-medium rounded-t-md border-t border-x focus-ring inline-flex items-center gap-1.5',
        active
          ? variant === 'note'
            ? 'bg-warn-500/10 text-warn-400 border-warn-500/30'
            : 'bg-ink-800 text-ink-100 border-ink-600'
          : 'text-ink-300 border-transparent hover:text-ink-100'
      )}
    >
      {children}
    </button>
  );
}

/**
 * Templates menu — transient popover anchored to a toolbar button.
 *
 * Empty list disables the trigger entirely (so the affordance reads
 * "no templates yet" only by being grey, not by listing zero items).
 * Pick → insertTemplate at caret in the parent's textarea, then close.
 */
function TemplatesMenu({
  templates,
  disabled,
  onPick,
}: {
  templates: EmailTemplate[];
  disabled?: boolean;
  onPick: (template: EmailTemplate) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isEmpty = templates.length === 0;

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        !menuRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled || isEmpty}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={isEmpty ? 'Templates (none yet)' : 'Insert template'}
        title={isEmpty ? 'No templates yet — add some in Settings → Templates' : undefined}
        data-testid="composer-templates-trigger"
        className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12.5px] text-ink-300 hover:text-ink-100 hover:bg-ink-800 focus-ring disabled:opacity-50"
      >
        <FileType size={13} />
        Templates
        <ChevronDown size={11} className="text-ink-400" />
      </button>
      {open && !isEmpty && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Insert template"
          className="absolute left-0 bottom-full mb-1 z-50 w-72 bg-ink-800 border border-ink-600 rounded-md shadow-pop overflow-hidden dropdown-menu-in"
          data-testid="composer-templates-menu"
        >
          <ul className="max-h-72 overflow-auto py-1">
            {templates.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onPick(t);
                    setOpen(false);
                  }}
                  data-testid={`composer-templates-pick-${t.id}`}
                  className="w-full text-left px-2.5 py-1.5 text-[13px] text-ink-200 hover:bg-ink-700 focus-ring"
                >
                  <div className="font-medium text-ink-100 truncate">
                    {t.name}
                  </div>
                  <div className="text-[11.5px] text-ink-400 line-clamp-1 mt-0.5">
                    {t.body}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Helper exposed for retry path on error. Used by ThreadView. */
export function useRetrySend(ticketNumber: string) {
  const sendMessage = useSendMessage();
  return (failed: CachedMessage) => {
    if (!failed.__optimisticId) return;
    sendMessage.mutate({
      ticketNumber,
      optimisticId: failed.__optimisticId,
      input: {
        type:
          failed.type === 'staff_email' || failed.type === 'internal_note'
            ? failed.type
            : 'staff_email',
        bodyText: failed.bodyText,
        bodyHtml: failed.bodyHtml,
      },
    });
  };
}
